import { useMutation } from '@apollo/client/react';
import {
  type ColumnDef,
  type OnChangeFn,
  type SortingState
} from '@tanstack/react-table';
import {
  BanIcon,
  CheckIcon,
  FolderOpenIcon,
  HourglassIcon,
  MoveDiagonalIcon,
  XIcon,
  ZapIcon
} from 'lucide-react';
import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { PaginationControls } from './PaginationControls';
import { UPDATE_OPPORTUNITY } from './queries';
import type {
  DealSelects,
  EmployeesAndDealSelects,
  GetDealOpportunitiesData,
  OpportunityNode
} from './types';
import { formatCurrencyValue } from './utils';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { TruncatedTextTooltip } from '@/components/TruncatedTextTooltip';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { getInitials } from '@/utils/getInitials';

interface TruncatedBadgeProps {
  badgeClassName?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  maxTextWidthClassName?: string;
  tooltipContent: string;
  variant?: 'green' | 'pastel-slate' | 'red' | 'yellow';
}

interface UpdateOpportunityResponse {
  updateOpportunity: {
    ok: boolean;
  };
}

interface UpdateOpportunityVariables {
  id: string;
  killedDealNotes?: string;
  killReason?: string;
  status: string;
}

const TruncatedBadge = ({
  badgeClassName,
  children,
  icon,
  maxTextWidthClassName,
  tooltipContent,
  variant
}: TruncatedBadgeProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  };

  return (
    <Tooltip open={isTruncated ? undefined : false}>
      <TooltipTrigger
        asChild
        onMouseEnter={handleMouseEnter}
      >
        <Badge
          className={cn(
            'max-w-full justify-start overflow-hidden',
            badgeClassName
          )}
          variant={variant}
        >
          {icon}
          <span
            className={cn(
              'block max-w-full min-w-0 truncate',
              maxTextWidthClassName
            )}
            ref={textRef}
          >
            {children}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

interface TopOfFunnelTabProps {
  currentPage: number;
  data1?: GetDealOpportunitiesData;
  dataEmployeesAndDealSelects: EmployeesAndDealSelects;
  dealSelects: DealSelects;
  loading1: boolean;
  newDealTrigger: ReactNode;
  onPageChange: (page: number) => void;
  onRefresh: () => Promise<unknown>;
  onSortingChange: OnChangeFn<SortingState>;
  showAllDeals?: boolean;
  sorting: SortingState;
}

const ITEMS_PER_PAGE = 20;

export const TopOfFunnelTab = ({
  currentPage,
  data1,
  dataEmployeesAndDealSelects,
  dealSelects,
  loading1,
  newDealTrigger,
  onPageChange,
  onRefresh,
  onSortingChange,
  showAllDeals = false,
  sorting
}: TopOfFunnelTabProps) => {
  const [killDialogDeal, setKillDialogDeal] = useState<null | OpportunityNode>(
    null
  );
  const [killReason, setKillReason] = useState('');
  const [killNotes, setKillNotes] = useState('');

  const [updateOpportunity, { loading: isUpdating }] = useMutation<
    UpdateOpportunityResponse,
    UpdateOpportunityVariables
  >(UPDATE_OPPORTUNITY);

  const handleMoveToActive = useCallback(
    async (deal: OpportunityNode) => {
      try {
        await updateOpportunity({
          variables: { id: deal.id, status: 'active' }
        });
        await onRefresh();
        toast.success('Deal moved to active.');
      } catch (error) {
        console.error(error);
        toast.error('Failed to move deal to active.');
      }
    },
    [updateOpportunity, onRefresh]
  );

  const handleKillDeal = useCallback(async () => {
    if (!killDialogDeal || !killReason) return;
    try {
      await updateOpportunity({
        variables: {
          id: killDialogDeal.id,
          killedDealNotes: killNotes || undefined,
          killReason,
          status: 'killed'
        }
      });
      await onRefresh();
      toast.success('Deal has been killed.');
      setKillDialogDeal(null);
      setKillReason('');
      setKillNotes('');
    } catch (error) {
      console.error(error);
      toast.error('Failed to kill deal.');
    }
  }, [killDialogDeal, killReason, killNotes, updateOpportunity, onRefresh]);

  const deals = data1?.opportunities.edges ?? [];
  const displayedDeals = showAllDeals
    ? deals.map(edge => edge.node)
    : deals
        .filter(edge => edge.node.stage === 'top_of_funnel')
        .map(edge => edge.node);

  const statusNameMap = useMemo(
    () =>
      new Map(
        dataEmployeesAndDealSelects.statusSelections.map(status => [
          status.value,
          status.name
        ])
      ),
    [dataEmployeesAndDealSelects.statusSelections]
  );

  // Get total count from the query response
  const totalDeals = data1?.opportunities.totalCount ?? 0;
  const totalPages = Math.ceil(totalDeals / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalDeals);

  const getPageNumbers = (): ('ellipsis' | number)[] => {
    const pages: ('ellipsis' | number)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis');
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    onPageChange(page);
  };

  const columns = useMemo<ColumnDef<OpportunityNode>[]>(
    () => [
      {
        cell: ({ row }) => (
          <div className="bg-background flex h-full w-full items-center justify-center">
            <Button
              asChild
              size="icon"
              variant="ghost"
            >
              <Link to={`/investments/direct/${row.original.id}/overview`}>
                <MoveDiagonalIcon />
              </Link>
            </Button>
          </div>
        ),
        enableSorting: false,
        header: () => <div className="bg-background h-full w-full" />,
        id: 'openDeal',
        meta: {
          cellClassName: 'p-0 bg-background',
          headerClassName: 'px-0 bg-background'
        },
        size: 52
      },
      {
        cell: ({ row }) => {
          const deal = row.original;
          const isTopOfFunnel = deal.stage === 'top_of_funnel';

          if (!isTopOfFunnel) return null;

          return (
            <div className="bg-background flex h-full w-full items-center justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    onClick={event => {
                      event.stopPropagation();
                    }}
                    size="icon"
                    variant="ghost"
                  >
                    <ZapIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={event => {
                      event.stopPropagation();
                      void handleMoveToActive(deal);
                    }}
                  >
                    <CheckIcon className="size-4" />
                    Move to Active
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={event => {
                      event.stopPropagation();
                      setKillDialogDeal(deal);
                      setKillReason('');
                      setKillNotes('');
                    }}
                    variant="destructive"
                  >
                    <BanIcon className="size-4" />
                    Kill
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        header: () => <div className="bg-background h-full w-full" />,
        id: 'actions',
        meta: {
          cellClassName: 'p-0 bg-background',
          headerClassName: 'px-0 bg-background'
        },
        size: 52
      },
      {
        accessorKey: 'name',
        cell: ({ row }) => (
          <div className="font-medium">
            <TruncatedTextTooltip>
              {row.original.name || '-'}
            </TruncatedTextTooltip>
          </div>
        ),
        header: () => (
          <div className="w-[160px] text-xs font-semibold">Deal Name</div>
        ),
        meta: {
          cellClassName: 'w-[160px] max-w-[160px]',
          headerClassName: 'w-[160px] max-w-[160px]'
        }
      },
      {
        accessorKey: 'targetEntityName',
        cell: ({ row }) => (
          <div className="font-medium">
            <TruncatedTextTooltip>
              {row.original.targetEntityName || '-'}
            </TruncatedTextTooltip>
          </div>
        ),
        enableSorting: false,
        header: () => (
          <div className="w-[160px] text-xs font-semibold">Company Name</div>
        )
      },
      {
        accessorKey: 'projectName',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.projectName || '-'}
          </TruncatedTextTooltip>
        ),
        header: () => (
          <div className="w-[140px] text-xs font-semibold">Project Name</div>
        )
      },
      {
        accessorKey: 'dealSource',
        cell: ({ row }) =>
          row.original.dealSource ? (
            <div className="flex items-center gap-x-2 overflow-hidden">
              <Avatar className="shrink-0">
                <AvatarFallback>
                  {getInitials(row.original.dealSource.name)}
                </AvatarFallback>
              </Avatar>
              <TruncatedTextTooltip>
                {row.original.dealSource.name}
              </TruncatedTextTooltip>
            </div>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[150px] text-xs font-semibold">
            Deal Source Firm
          </div>
        )
      },
      {
        accessorKey: 'mdTeamId',
        cell: ({ row }) =>
          row.original.mdTeamId ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="*:data-[slot=avatar]:ring-background flex -space-x-2 *:data-[slot=avatar]:ring-2">
                  <Avatar key={row.original.mdTeamId.id}>
                    <AvatarImage
                      alt="@shadcn"
                      src={row.original.mdTeamId.name}
                    />
                    <AvatarFallback>
                      {row.original.mdTeamId.name.split(' ')[0].charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                {row.original.mdTeamId.name}
              </TooltipContent>
            </Tooltip>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[70px] text-xs font-semibold">Deal Lead</div>
        )
      },
      {
        accessorKey: 'readableSmcIndustry',
        cell: ({ row }) =>
          row.original.readableSmcIndustry ? (
            <TruncatedBadge
              tooltipContent={row.original.readableSmcIndustry}
              variant="pastel-slate"
            >
              {row.original.readableSmcIndustry}
            </TruncatedBadge>
          ) : (
            '-'
          ),
        enableSorting: false,
        header: () => (
          <div className="w-[120px] text-xs font-semibold">Industry</div>
        )
      },
      {
        accessorKey: 'status',
        cell: ({ row }) => {
          const status = row.original.status;
          if (!status) {
            return null;
          }

          const statusLabel = statusNameMap.get(status) ?? status;

          if (status === 'new_unqualified' || status === 'long_term') {
            return (
              <TruncatedBadge
                icon={<HourglassIcon />}
                tooltipContent={statusLabel}
                variant="yellow"
              >
                {statusLabel}
              </TruncatedBadge>
            );
          }

          if (status === 'not_enough_information' || status === 'killed') {
            return (
              <TruncatedBadge
                icon={<XIcon />}
                tooltipContent={statusLabel}
                variant="red"
              >
                {statusLabel}
              </TruncatedBadge>
            );
          }

          if (status === 'active' || status === 'closed') {
            return (
              <TruncatedBadge
                icon={<CheckIcon />}
                tooltipContent={statusLabel}
                variant="green"
              >
                {statusLabel}
              </TruncatedBadge>
            );
          }

          return '-';
        },
        header: () => (
          <div className="w-[130px] text-xs font-semibold">Status</div>
        )
      },
      {
        accessorKey: 'createDate',
        cell: ({ row }) => (
          <TruncatedTextTooltip>
            {row.original.createDate
              ? row.original.createDate.slice(0, 10)
              : '-'}
          </TruncatedTextTooltip>
        ),
        header: () => <div className="text-xs font-semibold">Created on</div>,
        meta: {
          cellClassName: 'w-[110px] max-w-[110px]',
          headerClassName: 'w-[110px] max-w-[110px]'
        }
      },
      {
        accessorKey: 'totalProposedDebtInvestment',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.totalProposedDebtInvestment != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(row.original.totalProposedDebtInvestment)}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="truncate text-right text-xs font-semibold whitespace-nowrap">
            Proposed Debt Investment
          </div>
        ),
        meta: {
          cellClassName: 'w-[170px] max-w-[170px]',
          headerClassName: 'w-[170px] max-w-[170px]'
        }
      },
      {
        accessorKey: 'totalProposedEquityInvestment',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.totalProposedEquityInvestment != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(
                  row.original.totalProposedEquityInvestment
                )}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="truncate text-right text-xs font-semibold whitespace-nowrap">
            Proposed Equity Investment
          </div>
        ),
        meta: {
          cellClassName: 'w-[180px] max-w-[180px]',
          headerClassName: 'w-[180px] max-w-[180px]'
        }
      },
      {
        accessorKey: 'ltmEbitda',
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.ltmEbitda != null ? (
              <TruncatedTextTooltip>
                {formatCurrencyValue(row.original.ltmEbitda)}
              </TruncatedTextTooltip>
            ) : (
              '-'
            )}
          </div>
        ),
        header: () => (
          <div className="text-right text-xs font-semibold">EBITDA</div>
        ),
        meta: {
          cellClassName: 'w-[100px] max-w-[100px]',
          headerClassName: 'w-[100px] max-w-[100px]'
        }
      }
    ],
    [statusNameMap, handleMoveToActive]
  );

  return (
    <>
      <div className="grid h-full max-h-full grid-rows-[1fr_auto]">
        {loading1 || !data1?.opportunities ? (
          <div className="flex min-h-[60vh] w-full items-center justify-center">
            <Spinner className="size-8 text-blue-500" />
          </div>
        ) : displayedDeals.length === 0 ? (
          <Empty className="pt-52!">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FolderOpenIcon />
              </EmptyMedia>
              <EmptyTitle>No deals yet</EmptyTitle>
              <EmptyDescription>
                You haven't added any deal yet. Get started by adding your first
                deal.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>{newDealTrigger}</EmptyContent>
          </Empty>
        ) : (
          <DataTable
            className="min-w-max table-fixed"
            columns={columns}
            data={displayedDeals}
            enableColumnResizing
            getRowId={row => row.id}
            initialColumnPinning={{ left: ['openDeal', 'actions', 'name'] }}
            manualSorting
            onSortingChange={onSortingChange}
            persistColumnSizingKey="direct-underwriting-top-of-funnel-table"
            sorting={sorting}
          />
        )}
        <PaginationControls
          currentPage={currentPage}
          endIndex={endIndex}
          getPageNumbers={getPageNumbers}
          itemLabel="deals"
          itemsPerPage={ITEMS_PER_PAGE}
          onNextPage={handleNextPage}
          onPageClick={handlePageClick}
          onPreviousPage={handlePreviousPage}
          startIndex={startIndex}
          totalItems={totalDeals}
          totalPages={totalPages}
        />
      </div>

      <Dialog
        onOpenChange={open => {
          if (!open) {
            setKillDialogDeal(null);
            setKillReason('');
            setKillNotes('');
          }
        }}
        open={Boolean(killDialogDeal)}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Kill Deal</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <Select
                onValueChange={setKillReason}
                value={killReason}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {dealSelects.killReasonSelections.map(reason => (
                    <SelectItem
                      key={reason.id}
                      value={reason.value}
                    >
                      {reason.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Textarea
                onChange={event => {
                  setKillNotes(event.target.value);
                }}
                placeholder="Notes"
                value={killNotes}
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              onClick={() => {
                setKillDialogDeal(null);
                setKillReason('');
                setKillNotes('');
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={!killReason || isUpdating}
              onClick={() => {
                void handleKillDeal();
              }}
              variant="destructive"
            >
              {isUpdating ? 'Killing...' : 'Confirm Kill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
